import * as React from 'react';
import './chat-report.css';
import { useChatHost } from './host.tsx';
import { renderMarkdown } from './markdown.ts';
import { hasSqlChart, reportUrl } from './reports.ts';

// A saved chat report on its own page — PORTED from the old assistant/reports.js REPORT_HTML (the popup
// the chat's "Open ↗" and the rail open): title and meta, "📊 Generate dashboard" (when a chart carries
// its SQL) and "⎘ Share report", the Question, the answer, then every chart of the turn. A chart shows
// the SVG the host drew from its validated rows (or its rows as a table); a custom visualization runs in
// the host's sandbox, as in the chat. Only its owner reads it (the backend checks), signed in as usual.

interface Report {
  id: string;
  createdAt: string;
  userEmail: string;
  project: string;
  title: string;
  userPrompt: string | null;
  answer: string;
  charts: Record<string, unknown>[];
}

function ReportChart({ c }: { c: Record<string, unknown> }) {
  const host = useChatHost();
  const title = String(c.title || '(chart)') + (c.subtitle ? ' · ' + String(c.subtitle) : '');
  if (c.type === 'viz' && typeof c.code === 'string')
    return (
      <div className="chart-card" data-testid="chat-report-chart">
        <div className="chart-title">{title}</div>
        <div className="chart-wrap">
          {host.renderViz ? (
            host.renderViz({
              testid: 'chat-report-viz',
              title,
              code: c.code,
              data: c.data,
              ...(typeof c.height === 'number' ? { height: c.height } : {}),
            })
          ) : (
            <pre className="viz-code">{c.code}</pre>
          )}
        </div>
      </div>
    );
  const rows = (c.rows as Record<string, unknown>[] | undefined) ?? [];
  const cols = ((c.columns as { name: string }[] | undefined) ?? []).map((x) => x.name);
  const svg = typeof c.svg === 'string' && c.svg ? c.svg : null;
  return (
    <div className="chart-card" data-testid="chat-report-chart">
      <div className="chart-title">{title}</div>
      {svg ? (
        // Drawn by the api's analytics package from validated rows (escaped text).
        <div className="chart-wrap" dangerouslySetInnerHTML={{ __html: svg }} />
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--c-faint)' }}>No rows</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              {cols.map((k) => (
                <th key={k}>{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((k) => (
                  <td key={k}>{String(r[k] ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function ChatReportPage({ slug, id }: { slug: string; id: string }) {
  const host = useChatHost();
  const api = React.useMemo(() => host.clientOf(slug), [host, slug]);
  const [rep, setRep] = React.useState<Report | null>(null);
  const [error, setError] = React.useState<string | null>(id ? null : 'Missing report id.');
  const [shareLabel, setShareLabel] = React.useState('Share report');
  const [dashLabel, setDashLabel] = React.useState('Generate dashboard');
  const [dashBusy, setDashBusy] = React.useState(false);

  React.useEffect(() => {
    if (!id) return;
    void api
      .GET('/reports/{id}', { params: { path: { id } } })
      .then((r) => {
        if (!r.response.ok || !r.data) setError(`Failed to load report (HTTP ${r.response.status})`);
        else {
          setRep(r.data as Report);
          document.title = 'Report · ' + (r.data.title || host.brand || 'Assistant');
        }
      })
      .catch((e: Error) => setError(`Failed to load report (${e.message})`));
  }, [slug, id]);

  if (!rep)
    return (
      <div className="cx-report-page">
        <div className="cx-report">
          <div id="status" className={error ? 'error' : ''} data-testid="chat-report-status">
            {error ?? 'Loading…'}
          </div>
        </div>
      </div>
    );

  return (
    <div className="cx-report-page">
      <div className="cx-report" data-testid="chat-report">
        <header>
          <div className="head-main">
            <h1 id="title">{rep.title || '(untitled)'}</h1>
            <div className="meta">
              <span id="created">{new Date(rep.createdAt).toLocaleString()}</span>
              {rep.project && <span id="project">{'project: ' + rep.project}</span>}
              {rep.userEmail && <span id="author">{'by ' + rep.userEmail}</span>}
            </div>
          </div>
          <div className="head-actions">
            {hasSqlChart(rep.charts) && host.generateDashboard && (
              <button
                id="dashboard-btn"
                className="share-btn"
                title="Build a live, interactive dashboard from this report"
                style={{ marginRight: 8, background: '#4f46e5' }}
                disabled={dashBusy}
                data-testid="chat-report-dashboard"
                onClick={async () => {
                  setDashBusy(true);
                  setDashLabel('Building…');
                  try {
                    await host.generateDashboard!(slug, { title: rep.title, charts: rep.charts });
                    setDashLabel('✓ Dashboard ready');
                    setTimeout(() => {
                      setDashBusy(false);
                      setDashLabel('Generate dashboard');
                    }, 2500);
                  } catch (e) {
                    setDashLabel('Failed: ' + ((e as Error).message || e));
                    setTimeout(() => {
                      setDashBusy(false);
                      setDashLabel('Generate dashboard');
                    }, 3500);
                  }
                }}
              >
                <span>📊</span>
                <span id="dashboard-label">{dashLabel}</span>
              </button>
            )}
            <button
              id="share-btn"
              className={'share-btn' + (shareLabel === 'Link copied' ? ' copied' : '')}
              title="Copy link to this report"
              onClick={async () => {
                const link = reportUrl(host, slug, rep.id);
                try {
                  await navigator.clipboard.writeText(link);
                  setShareLabel('Link copied');
                  setTimeout(() => setShareLabel('Share report'), 1800);
                } catch {
                  window.prompt('Share link:', link);
                }
              }}
            >
              <span>⎘</span>
              <span id="share-label">{shareLabel}</span>
            </button>
          </div>
        </header>
        {rep.userPrompt && (
          <div id="prompt">
            <div id="prompt-label">Question</div>
            <div id="prompt-text">{rep.userPrompt}</div>
          </div>
        )}
        <div id="answer" dangerouslySetInnerHTML={{ __html: renderMarkdown(rep.answer || '') }} />
        <div id="charts">
          {rep.charts.map((c, i) => (
            <ReportChart key={i} c={c} />
          ))}
        </div>
        <footer>{host.brand ?? 'Assistant'} analyst report</footer>
      </div>
    </div>
  );
}
